import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { useAuth, can } from "@/lib/auth";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, CheckCircle2, FileDown, MessageCircle, ExternalLink } from "lucide-react";
import { WhatsAppDialog, downloadAuthFile } from "@/components/WhatsAppDialog";
import { portalForReturnType } from "@/pages/Portals";

const statusBadge = {
  pending: { bg: "bg-zinc-100", color: "text-zinc-700", border: "border-zinc-300", label: "Pending" },
  in_progress: { bg: "bg-[#FFFAEB]", color: "text-[#B54708]", border: "border-[#F79009]", label: "In Progress" },
  filed: { bg: "bg-[#ECFDF3]", color: "text-[#027A48]", border: "border-[#039855]", label: "Filed" },
  late: { bg: "bg-[#FEF3F2]", color: "text-[#B42318]", border: "border-[#D92D20]", label: "Late" },
};

const returnTypes = ["GSTR-1", "GSTR-3B", "GSTR-9", "ITR-1", "ITR-2", "ITR-3", "ITR-4", "ITR-5", "ITR-6", "TDS-24Q", "TDS-26Q", "TDS-27Q", "Advance Tax", "Form 16", "ROC-MGT-7", "ROC-AOC-4", "Audit Report"];

const empty = { client_id: "", return_type: "GSTR-3B", period: "", due_date: "", status: "pending", assigned_to: "", notes: "" };

export default function TaxReturns() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [team, setTeam] = useState([]);
  const [filter, setFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [waOpen, setWaOpen] = useState(false);
  const [waCtx, setWaCtx] = useState(null);

  const load = async () => {
    const [a, b, c] = await Promise.all([api.get("/tax-returns"), api.get("/clients"), api.get("/users")]);
    setItems(a.data);
    setClients(b.data);
    setTeam(c.data);
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (r) => { setEditing(r); setForm({ ...empty, ...r, assigned_to: r.assigned_to || "" }); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, assigned_to: form.assigned_to || null };
      if (editing) {
        await api.put(`/tax-returns/${editing.id}`, payload);
        toast.success("Return updated");
      } else {
        await api.post("/tax-returns", payload);
        toast.success("Return added");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    }
  };

  const markFiled = async (r) => {
    await api.put(`/tax-returns/${r.id}`, {
      client_id: r.client_id,
      return_type: r.return_type,
      period: r.period,
      due_date: r.due_date,
      status: "filed",
      assigned_to: r.assigned_to,
      notes: r.notes,
    });
    toast.success("Marked as filed");
    load();
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this return?")) return;
    await api.delete(`/tax-returns/${id}`);
    toast.success("Deleted");
    load();
  };

  const downloadPdf = async (r) => {
    try {
      await downloadAuthFile(`/tax-returns/${r.id}/pdf`, `return-${r.return_type}-${r.period}.pdf`);
      toast.success("PDF downloaded");
    } catch (e) {
      toast.error("Could not download PDF");
    }
  };

  const shareWhatsApp = (r) => {
    const client = clients.find(c => c.id === r.client_id);
    const statusTxt = r.status === "filed" ? "has been filed" : `is ${r.status.replace("_", " ")}`;
    const msg = `Hello ${r.client_name},\n\nFiling status update:\n\nReturn Type: ${r.return_type}\nPeriod: ${r.period}\nDue Date: ${r.due_date}\nStatus: ${r.status.toUpperCase().replace("_", " ")}\n\nYour ${r.return_type} for ${r.period} ${statusTxt}. ${r.status !== "filed" ? "Kindly share the required documents at the earliest to ensure timely filing." : "Acknowledgement will be shared shortly."}\n\nRegards,\nTAXINPHO Office`;
    setWaCtx({ phone: client?.phone || "", message: msg, label: `return-${r.return_type}-${r.period}.pdf` });
    setWaOpen(true);
  };

  const filtered = filter === "all" ? items : items.filter(i => i.status === filter);

  return (
    <div className="p-6 md:p-8 max-w-[1600px]">
      <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="label-caps mb-2">Compliance</p>
          <h1 className="font-display font-black text-4xl tracking-tighter">Tax Returns</h1>
          <p className="text-zinc-600 mt-2">GST, ITR, TDS, ROC and audit filings.</p>
        </div>
        {can(user, "returns_write") && (
          <button onClick={openNew} data-testid="new-return" className="bg-zinc-900 text-white px-4 py-2.5 font-semibold flex items-center gap-2 hover:bg-zinc-800">
            <Plus size={16} /> New Return
          </button>
        )}
      </div>

      <div className="flex gap-2 mb-4 flex-wrap">
        {["all", "pending", "in_progress", "filed", "late"].map(s => (
          <button key={s} onClick={() => setFilter(s)} data-testid={`filter-${s}`}
            className={`px-3 py-1.5 text-sm font-semibold border transition-colors ${filter === s ? "bg-zinc-900 text-white border-zinc-900" : "bg-white border-zinc-300 hover:bg-zinc-50"}`}>
            {s === "all" ? "All" : statusBadge[s]?.label || s}
          </button>
        ))}
      </div>

      <div className="bg-white border border-zinc-200 overflow-x-auto">
        <table className="w-full">
          <thead className="bg-zinc-50 border-b border-zinc-200">
            <tr>
              <th className="text-left px-6 py-3 label-caps">Client</th>
              <th className="text-left px-6 py-3 label-caps">Type</th>
              <th className="text-left px-6 py-3 label-caps">Period</th>
              <th className="text-left px-6 py-3 label-caps">Due Date</th>
              <th className="text-left px-6 py-3 label-caps">Assigned</th>
              <th className="text-left px-6 py-3 label-caps">Status</th>
              <th className="text-right px-6 py-3 label-caps"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && <tr><td colSpan="7" className="p-10 text-center text-sm text-zinc-500 font-mono">[ nothing here ]</td></tr>}
            {filtered.map(r => {
              const s = statusBadge[r.status] || statusBadge.pending;
              const isOverdue = r.status !== "filed" && new Date(r.due_date) < new Date();
              return (
                <tr key={r.id} className="border-b border-zinc-100 hover:bg-zinc-50">
                  <td className="px-6 py-3 font-medium text-sm">{r.client_name}</td>
                  <td className="px-6 py-3 font-mono text-xs">{r.return_type}</td>
                  <td className="px-6 py-3 text-sm">{r.period}</td>
                  <td className={`px-6 py-3 text-sm ${isOverdue ? "text-[#D92D20] font-semibold" : ""}`}>{r.due_date}</td>
                  <td className="px-6 py-3 text-sm">{r.assigned_to_name || "—"}</td>
                  <td className="px-6 py-3"><span className={`inline-block px-2.5 py-0.5 text-xs font-medium border ${s.bg} ${s.color} ${s.border}`}>{s.label}</span></td>
                  <td className="px-6 py-3 text-right whitespace-nowrap">
                    {portalForReturnType(r.return_type) && (
                      <button
                        onClick={() => window.open(portalForReturnType(r.return_type).url, "_blank", "noopener,noreferrer")}
                        title={`Open ${portalForReturnType(r.return_type).name}`}
                        data-testid={`portal-${r.id}`}
                        className="p-1.5 hover:bg-blue-50 text-[#002FA7] mr-1"
                      >
                        <ExternalLink size={14} />
                      </button>
                    )}
                    <button onClick={() => downloadPdf(r)} title="Download PDF" data-testid={`pdf-return-${r.id}`} className="p-1.5 hover:bg-zinc-100 mr-1"><FileDown size={14} /></button>
                    <button onClick={() => shareWhatsApp(r)} title="Send on WhatsApp" data-testid={`wa-return-${r.id}`} className="p-1.5 hover:bg-green-50 text-[#25D366] mr-1"><MessageCircle size={14} /></button>
                    {can(user, "returns_write") && r.status !== "filed" && (
                      <button onClick={() => markFiled(r)} className="p-1.5 hover:bg-green-50 text-[#039855] mr-1" title="Mark filed" data-testid={`mark-filed-${r.id}`}><CheckCircle2 size={14} /></button>
                    )}
                    {can(user, "returns_write") && (
                      <button onClick={() => openEdit(r)} className="p-1.5 hover:bg-zinc-100 mr-1"><Pencil size={14} /></button>
                    )}
                    {can(user, "delete") && (
                      <button onClick={() => remove(r.id)} className="p-1.5 hover:bg-red-50 text-[#D92D20]"><Trash2 size={14} /></button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {open && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-white border border-zinc-200 w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-zinc-200 flex items-center justify-between">
              <h2 className="font-display font-bold text-xl tracking-tight">{editing ? "Edit Return" : "New Return"}</h2>
              <button onClick={() => setOpen(false)}><X size={18} /></button>
            </div>
            <form onSubmit={save} className="p-6 space-y-4">
              <div>
                <label className="label-caps block mb-2">Client *</label>
                <select required value={form.client_id} onChange={e => setForm({ ...form, client_id: e.target.value })} className="w-full border border-zinc-300 px-3 py-2 bg-white">
                  <option value="">Select client</option>
                  {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label-caps block mb-2">Return Type *</label>
                  <select required value={form.return_type} onChange={e => setForm({ ...form, return_type: e.target.value })} className="w-full border border-zinc-300 px-3 py-2 bg-white font-mono">
                    {returnTypes.map(t => <option key={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className="label-caps block mb-2">Period *</label>
                  <input required value={form.period} onChange={e => setForm({ ...form, period: e.target.value })} placeholder="e.g. 2025-Q3 or AY 2025-26" className="w-full border border-zinc-300 px-3 py-2 font-mono" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label-caps block mb-2">Due Date *</label>
                  <input required type="date" value={form.due_date} onChange={e => setForm({ ...form, due_date: e.target.value })} className="w-full border border-zinc-300 px-3 py-2" />
                </div>
                <div>
                  <label className="label-caps block mb-2">Status</label>
                  <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })} className="w-full border border-zinc-300 px-3 py-2 bg-white">
                    <option value="pending">Pending</option>
                    <option value="in_progress">In Progress</option>
                    <option value="filed">Filed</option>
                    <option value="late">Late</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="label-caps block mb-2">Assign to</label>
                <select value={form.assigned_to} onChange={e => setForm({ ...form, assigned_to: e.target.value })} className="w-full border border-zinc-300 px-3 py-2 bg-white">
                  <option value="">Unassigned</option>
                  {team.map(u => <option key={u.id} value={u.id}>{u.name} ({u.role})</option>)}
                </select>
              </div>
              <div>
                <label className="label-caps block mb-2">Notes</label>
                <textarea rows="3" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} className="w-full border border-zinc-300 px-3 py-2" />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setOpen(false)} className="px-4 py-2 border border-zinc-300 font-semibold">Cancel</button>
                <button type="submit" data-testid="save-return" className="bg-zinc-900 text-white px-4 py-2 font-semibold">Save</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <WhatsAppDialog
        open={waOpen}
        onClose={() => setWaOpen(false)}
        defaultPhone={waCtx?.phone}
        defaultMessage={waCtx?.message}
        attachLabel={waCtx?.label}
      />
    </div>
  );
}
